import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DefaultLayout from '../../layout/DefaultLayout';
import Loading from '../../components/Loading';
import Alert from '../../components/Alert';
import beneficiarioService from '../../services/beneficiarioService';
import { useGlobalState } from '../../state';
import { FaUserCheck } from 'react-icons/fa';
import Breadcrumb from '../../components/Breadcrumbs/Breadcrumb';

type Supervivencia = {
  periodo: string;
  estado: string;
  fechaEmision: string;
  fechaRetorno?: string | null;
  tipoSupervivencia: string;
  parentesco?: string | null;
};

const Supervivencia: React.FC = () => {
  const navigate = useNavigate();
  const [user] = useGlobalState('user');

  const [ultimaPropia, setUltimaPropia] = useState<Supervivencia | undefined>(undefined);
  const [ultimasFamiliares, setUltimasFamiliares] = useState<Supervivencia[] | undefined>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [requiereTramite, setRequiereTramite] = useState<boolean>(false);
  const [periodo, setPeriodo] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (user === undefined) return; // still checking auth
    if (!user) {
      navigate('/auth/signin');
      return;
    }

    const fetchHistorial = async () => {
      setLoading(true);
      setError(undefined);
      try {
        const res: any = await beneficiarioService.getSupervivencias(
          user.datosUsuario.beneficiario?.afiliadoId,
          user.datosUsuario.beneficiario?.tipoBeneficiarioId
        );

        if (res?.success) {
          const data = res.data as {
            supervivenciasPropias: Supervivencia[];
            supervivenciasFamiliares: Supervivencia[];
          };

          // ultima propia por periodo mayor
          const ultima = (data.supervivenciasPropias || []).reduce<Supervivencia | undefined>((acc, cur) => {
            if (!acc) return cur;
            return Number(cur.periodo) > Number(acc.periodo) ? cur : acc;
          }, undefined);
          setUltimaPropia(ultima);
          if (ultima) {
            setRequiereTramite(ultima.estado === 'E');
            setPeriodo(ultima.periodo);
          }

          // ultimas familiares por tipo (por periodo mayor)
          const map = new Map<string, Supervivencia>();
          (data.supervivenciasFamiliares || []).forEach(s => {
            const existing = map.get(s.tipoSupervivencia);
            if (!existing || Number(s.periodo) > Number(existing.periodo)) {
              map.set(s.tipoSupervivencia, s);
            }
          });
          setUltimasFamiliares(Array.from(map.values()));
        } else {
          setError(res?.errors?.[0] ?? 'Error al consultar los trámites de supervivencia');
        }
      } catch (ex: any) {
        setError(ex?.message ?? 'Error al consultar los trámites de supervivencia');
      } finally {
        setLoading(false);
      }
    };

    fetchHistorial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const iniciarTramite = () => {
    if (!user) return;
    const qs = new URLSearchParams({
      idBeneficiario: String(user.datosUsuario.beneficiario?.afiliadoId ?? ''),
      idTipoBeneficio: String(user.datosUsuario.beneficiario?.tipoBeneficiarioId ?? ''),
      tipoTramiteSupervivencia: '0',
      periodo: periodo ?? '',
    }).toString();
    navigate(`/supervivencia/verificarSupervivenciaAWS?${qs}`);
  };

  const iniciarTramiteFamiliar = (s: Supervivencia) => {
    if (!user) return;
    const qs = new URLSearchParams({
    idBeneficiario: String(user.datosUsuario.beneficiario?.afiliadoId ?? ''),
    idTipoBeneficio: String(user.datosUsuario.beneficiario?.tipoBeneficiarioId ?? ''),
      tipoTramiteSupervivencia: String(s.tipoSupervivencia ?? ''),
      periodo: s.periodo ?? '',
    }).toString();
    navigate(`/supervivencia/verificarSupervivenciaAWS?${qs}`);
  };

  return (
      <DefaultLayout>


          <Breadcrumb pageName="Trámite de Supervivencia" />

      <div className="p-2">
        <div className="max-w-6xl mx-auto">
            {error && <Alert type="failure" message={error} />}

{/*            <h1 className="text-2xl font-semibold mb-6">Supervivencia</h1>   */}      

          {loading && <Loading statusText="Cargando" />}

          

          {!loading && !error && (
            <>
            <h2 className="text-lg font-medium mb-2">Supervivencia Personal</h2>
            <div className="mb-6 text-sm text-gray-600 text-center bg-white p-4 dark:bg-boxdark rounded-xl">
              <p className="text-sm text-gray-600 mb-4 text-center bg-white p-4 dark:bg-boxdark rounded-xl">
                {requiereTramite
                  ? 'Usted tiene pendiente su trámite de supervivencia. Recuerde que debe iniciar su trámite para poder percibir sus haberes.'
                  : 'Usted tiene su trámite de supervivencia actualizado!'}
              </p>

              {requiereTramite && (
            
                  <button
                    onClick={iniciarTramite}
                    className="px-4 py-2 rounded bg-primario-celeste-800 text-white font-semibold hover:bg-primario-azul-900"
                  >
                    Iniciar Trámite Supervivencia Personal
                  </button>
          
                              )}
             </div>

              <h3 className="text-lg font-medium mt-6 mb-2">Supervivencias Familiares</h3>

              {ultimasFamiliares && ultimasFamiliares.length > 0 ? (
                <div className="space-y-6 text-sm text-gray-600 mb-4 text-center bg-white p-4 dark:bg-boxdark rounded-xl">
                  {ultimasFamiliares.map((fam, idx) => (
                    <div key={idx} className="bg-white dark:bg-boxdark p-4 rounded shadow-sm">
                      <div className="flex items-start md:items-center md:justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="flex items-center justify-center rounded-full h-12 w-12 border">
                            <FaUserCheck className="text-gray-700" />
                          </div>
                          <div>
                            <div className="text-sm font-semibold">{fam.tipoSupervivencia}</div>
                            <div className="text-xs text-gray-500 mt-1">
                              {fam.estado === 'R' ? 'Aprobada' : fam.estado === 'C' ? 'En Revisión' : 'Pendiente'}
                            </div>
                          </div>
                        </div>

                        <div className="text-sm text-gray-600 text-right">
                          {fam.fechaRetorno ? new Date(fam.fechaRetorno).toLocaleDateString() : new Date(fam.fechaEmision).toLocaleDateString()}
                        </div>
                      </div>

                      <p className="text-sm text-gray-600 mt-3 text-center">
                        {fam.estado === 'E'
                          ? `El familiar en carácter de ${fam.parentesco} tiene pendiente su trámite de supervivencia. Recuerde realizar el trámite.`
                          : `El familiar en carácter de ${fam.parentesco} tiene su supervivencia actualizada!`}
                      </p>

                      {fam.estado === 'E' && (
                        <div className="mt-3 text-center">
                          <button
                            onClick={() => iniciarTramiteFamiliar(fam)}
                            className="px-3 py-2 rounded bg-primario-celeste-800 text-white"
                          >
                            Iniciar Trámite Supervivencia {fam.parentesco}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-600 mb-4 text-center bg-white p-4 dark:bg-boxdark rounded-xl">
                    No hay supervivencias familiares registradas.
                </p>
              )}
            </>
                  )}

                  <div className="flex gap-3 justify-center mt-8">
                      <button
                          onClick={() => navigate(-1)}
                          className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded"
                      >
                          Volver
                      </button>
                  </div>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default Supervivencia;